import React, { useCallback } from "react";
import { Saddle } from "@tvg/design-system";
import {
  BettingInterest,
  Runner,
  Willpay,
  WillpayPayout
} from "@tvg/ts-types/Race";
import formatCurrency from "@tvg/formatter/currency";
import {
  ContentTable,
  TR,
  TD,
  PayoutCellContainer,
  SaddleNumberContainer
} from "./styled-components";
import { MAX_AMOUNT } from "../PoolsCard/constants";
import TooltipExceededAmounts from "../PoolsCard/components/TooltipExceededAmounts";

interface Props {
  selectedWillPay: string;
  willPays: Willpay[];
  bettingInterests?: BettingInterest[];
}

const ContentTableWillPays = ({
  selectedWillPay,
  willPays,
  bettingInterests
}: Props) => {
  const payouts = willPays[+selectedWillPay]?.payouts || [];
  const half = Math.ceil(payouts.length / 2);
  const firstHalf = payouts.slice(0, half);
  const secondHalf = payouts.slice(half);

  const newPayouts: WillpayPayout[] = firstHalf.reduce(
    (result: WillpayPayout[], payout, index) =>
      result.concat(payout, secondHalf[index]),
    []
  );

  // This function is for see if the first entry is scratched, and if is and exists a second entry the payout are available anyway.
  const coupledRunners = (bettingInterest: BettingInterest | undefined) =>
    bettingInterest?.runners[1] &&
    bettingInterest?.runners.length >= 1 &&
    bettingInterest?.runners[0].scratched
      ? bettingInterest?.runners[1].scratched
      : bettingInterest?.runners[0].scratched;

  const getRunnersAndPayoutsForRow = (rowNumber: number) => {
    const startIndex = rowNumber * 2;
    const endIndex = Math.min((rowNumber + 1) * 2, payouts.length);

    return newPayouts.slice(startIndex, endIndex).map((payout) => {
      const bettingInterest = bettingInterests?.find(
        (bi) => bi.biNumber === payout.bettingInterestNumber
      );
      const allBiScratched = bettingInterest?.runners.every(
        (runner) => runner.scratched
      );

      const handlePayoutAmount = () =>
        payout.payoutAmount > MAX_AMOUNT ? (
          <TooltipExceededAmounts amount={payout.payoutAmount} />
        ) : (
          `$${formatCurrency(payout.payoutAmount, 2)}`
        );

      const isCoupledRunner =
        bettingInterest?.runners && bettingInterest?.runners.length > 1;

      const isAllCoupledRunnersScratched = isCoupledRunner && allBiScratched;

      const renderSadddleRunner = (biRunner: Runner) => (
        <Saddle
          size="s"
          saddleColor={bettingInterest?.saddleColor}
          numberColor={bettingInterest?.numberColor}
          number={biRunner.runnerId}
          isScratched={coupledRunners(bettingInterest)}
          m="space-4"
          mr={0}
          qaLabel={`saddle-container-${biRunner.runnerId}`}
        />
      );

      const renderValidRunners = useCallback(
        () =>
          bettingInterest?.runners.map((biRunner) => {
            const isRunnerScratched = biRunner.scratched;
            const isValidCoupledRunner = isCoupledRunner && !isRunnerScratched;

            return (
              (isValidCoupledRunner || !isCoupledRunner) &&
              renderSadddleRunner(biRunner)
            );
          }),
        [JSON.stringify(bettingInterest?.runners), isCoupledRunner]
      );

      const renderScratchedCoupledRunner = useCallback(() => {
        const biRunner = bettingInterest?.runners[0];
        return biRunner ? renderSadddleRunner(biRunner) : <></>;
      }, [JSON.stringify(bettingInterest?.runners)]);

      const renderRunners = isAllCoupledRunnersScratched
        ? renderScratchedCoupledRunner()
        : renderValidRunners();

      return (
        <TD
          data-qa-label={`willPays-cell-${bettingInterest?.biNumber}`}
          key={bettingInterest?.biNumber.toString()}
        >
          <PayoutCellContainer data-qa-label="payout-cell-container">
            <SaddleNumberContainer data-qa-label="willPays-saddle-container">
              {renderRunners}
            </SaddleNumberContainer>
            {coupledRunners(bettingInterest) ? "-" : handlePayoutAmount()}
          </PayoutCellContainer>
        </TD>
      );
    });
  };

  const tableRows = Array.from({ length: half }, (_, index) => (
    <TR
      data-qa-label="willPays-table-row"
      key={`willPays-table-row-${index.toString()}`}
    >
      {getRunnersAndPayoutsForRow(index)}
    </TR>
  ));

  return (
    <ContentTable data-qa-label="willPays-table">
      <tbody>{tableRows}</tbody>
    </ContentTable>
  );
};

export default ContentTableWillPays;
